import { useQueryClient } from '@tanstack/react-query'
import { ChevronDown, Loader2, Phone } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { callingKeys, useCallingMe } from '@/hooks/useCalling'
import { startCall } from '@/services/api/calling.service'
import { ApiError } from '@/services/api/client'
import { useCallStore } from '@/stores/call.store'
import { useWorkspaceStore } from '@/stores/workspace.store'
import type { Call } from '@/types/api/calling'
import { cn } from '@/utils/cn'
import { getErrorMessage } from '@/utils/error-parser'
import { isNativeApp } from '@/utils/platform'

import { callOptions, type CallOption } from '../callText'
import { startSimCallOnThisPhone } from '../sim/simCallFlow'

interface CallButtonProps {
  recordId: string
  /** The PHONE field to call; the server reads the number from the record. */
  phoneFieldId: string
  /** The field's current value (the button hides when it is empty). */
  value: string | null | undefined
  className?: string
}

/**
 * Calls a record's phone number. Hidden unless calling is on the plan, the user has call:make and
 * at least one way to call works for them; with several ways it opens a small menu.
 */
export function CallButton({ recordId, phoneFieldId, value, className }: CallButtonProps) {
  const { data: me } = useCallingMe()
  const queryClient = useQueryClient()
  const workspaceId = useWorkspaceStore((s) => s.currentWorkspaceId)
  const [busy, setBusy] = useState(false)
  const native = isNativeApp()
  const options = callOptions(me, { native })

  if (!value || options.length === 0) return null

  const start = async (option: CallOption) => {
    setBusy(true)
    try {
      const call: Call =
        option.mode === 'SIM' && native
          ? await startSimCallOnThisPhone(recordId, phoneFieldId, option.purpose)
          : await startCall({
              recordId,
              phoneFieldId,
              mode: option.mode,
              purpose: option.purpose,
              dialOnThisDevice: option.mode === 'SIM' ? false : undefined,
            })
      queryClient.setQueryData(callingKeys.call(workspaceId, call.id), call)
      useCallStore.getState().addCall(call.id)
      if (call.status === 'ENDED' && call.outcome === 'FAILED') {
        toast.error(call.hangupCause ?? 'The call could not be started')
      }
    } catch (error) {
      const activeCallId = error instanceof ApiError ? error.body?.activeCallId : undefined
      if (error instanceof ApiError && error.status === 409 && typeof activeCallId === 'string') {
        toast.info('You already have a call in progress')
        useCallStore.getState().addCall(activeCallId)
      } else {
        toast.error(error instanceof ApiError ? getErrorMessage(error, 'Could not start the call')
          : error instanceof Error ? error.message : 'Could not start the call')
      }
    } finally {
      setBusy(false)
    }
  }

  const buttonClass = cn(
    'inline-flex shrink-0 items-center gap-1 rounded-lg border border-lime-200 bg-lime-50 px-2 py-1 text-xs font-semibold text-lime-700',
    'transition-colors hover:bg-lime-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-lime-400 disabled:opacity-60',
    className
  )
  const icon = busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Phone className="h-3.5 w-3.5" />

  if (options.length === 1) {
    return (
      <button
        type="button"
        className={buttonClass}
        disabled={busy}
        title={options[0].label}
        aria-label={options[0].label}
        onClick={(e) => {
          e.stopPropagation()
          void start(options[0])
        }}
      >
        {icon}
        <span>Call</span>
      </button>
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={buttonClass}
          disabled={busy}
          aria-label="Call this number"
          onClick={(e) => e.stopPropagation()}
        >
          {icon}
          <span>Call</span>
          <ChevronDown className="h-3 w-3" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
        {options.map((option) => (
          <DropdownMenuItem key={`${option.mode}-${option.purpose ?? ''}`} onSelect={() => void start(option)}>
            <Phone className="mr-2 h-3.5 w-3.5 text-lime-600" />
            {option.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
